import * as React from "react";
import { useEffect, useState } from "react";
import styled from "styled-components";
import {
  CompanyData,
  ProjectData,
  ProjectFormData,
  ProjectStatus,
  SaveFailReason,
  SaveProjectsResponse,
} from ".";
import classnames from "classnames";
import ModalButton from "../common/ModalButton";
import SaveProjectsModal from "./SaveProjectsModal";

export interface ProjectSummaryFormData extends ProjectFormData {
  serverStatus?: ProjectStatus;
}

interface Summary {
  /** Projects with a changed status */
  changedProjects: ProjectSummaryFormData[];
}

interface Props {
  company: CompanyData<ProjectFormData>;
  showAll?: boolean;
  onStatusChanged: (
    companyId: number,
    projectId: number,
    newStatus: ProjectStatus
  ) => void;
  onSaveProjects: (
    changedProjects: ProjectFormData[]
  ) => Promise<SaveProjectsResponse | null>;
}

const Wrapper = styled.div`
  & .name {
    font-size: 1.2rem;
    font-weight: bold;
  }
`;

const Table = styled.table`
  padding-bottom: 1rem;
`;

const StyledModalButton = styled(ModalButton)`
  margin-bottom: 0;
`;

const Projects = ({ company, onStatusChanged, onSaveProjects }: Props) => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [summary, setSummary] = useState({
    changedProjects: [],
  } as Summary);

  useEffect(() => {
    if (!showSaveModal) {
      // update status to serverStatus for projects that had their status changed by another user
      setSummary({
        ...summary,
        changedProjects: summary.changedProjects.map((project) => {
          if (
            project.serverStatus != null &&
            project.status != project.serverStatus
          ) {
            return {
              ...project,
              status: project.serverStatus,
            };
          }

          return project;
        }),
      });
    } else {
      // Sort changed projects by name when they're about to be shown
      setSummary({
        ...summary,
        changedProjects: [...summary.changedProjects].sort((a, b) =>
          a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ? 1 : -1
        ),
      });
    }
  }, [showSaveModal]);

  const updateSummary = (
    project: ProjectFormData,
    newStatus: ProjectStatus
  ) => {
    if (newStatus !== project.status) {
      // if the project is already changed, update the existing summary project status
      if (summary.changedProjects.find((p) => p.id === project.id)) {
        const newSummary = {
          ...summary,
          changedProjects: summary.changedProjects.map((sumProject) => {
            if (project.id !== sumProject.id) {
              return sumProject;
            }

            return {
              ...sumProject,
              newStatus,
            };
          }),
        };

        setSummary(newSummary);
        return;
      }

      // add new summary project
      const newSummary = {
        ...summary,
        changedProjects: [
          ...summary.changedProjects,
          {
            id: project.id,
            name: project.name,
            orderDate: project.orderDate,
            status: project.status,
            newStatus,
          },
        ],
      };

      setSummary(newSummary);
    } else {
      // remove from summary if it's added
      if (!summary.changedProjects.find((p) => p.id === project.id)) {
        return;
      }

      const newSummary = {
        ...summary,
        changedProjects: summary.changedProjects.filter(
          (p) => p.id !== project.id
        ),
      };

      setSummary(newSummary);
    }
  };

  const internalOnStatusChanged = (
    project: ProjectFormData,
    newStatus: ProjectStatus
  ) => {
    updateSummary(project, newStatus);
    onStatusChanged(company.id, project.id, newStatus);
  };

  const internalOnSaveProjects = async (
    changedProjects: ProjectFormData[]
  ): Promise<SaveProjectsResponse | null> => {
    let result = await onSaveProjects(changedProjects);

    if (result == null) {
      return result;
    }

    if (result.status === 200) {
      setSummary({ changedProjects: [] });
    } else if (result.status == 409) {
      let newSummary = { ...summary };

      // remove successful saves
      if (result.successful?.length) {
        newSummary = {
          ...newSummary,
          changedProjects: newSummary.changedProjects.filter(
            (p) => result!.successful!.indexOf(p.id) === -1
          ),
        };
      }

      // update current status of projects with changed status
      if (result.failed?.length) {
        newSummary = {
          ...newSummary,
          changedProjects: newSummary.changedProjects.map((project) => {
            const failData = result!.failed!.find((p) => p.id === project.id);

            if (
              failData == null ||
              failData.reason !== SaveFailReason.StatusChanged
            ) {
              return project;
            }

            return {
              ...project,
              serverStatus: failData.newStatus!,
            };
          }),
        };
      }

      setSummary(newSummary);
    }

    return result;
  };

  return (
    <Wrapper>
      <div className="name">{company.name}</div>
      <Table className="projects display table-small dataTable dtr-inline">
        <thead>
          <tr>
            <td>Namn</td>
            <td>Orderdatum</td>
            <td>Status</td>
            <td>Ny status</td>
          </tr>
        </thead>
        <tbody>
          {company.projects.map((project) => (
            <TableRow
              key={project.id}
              project={project}
              onStatusChanged={(newStatus) =>
                internalOnStatusChanged(project, newStatus)
              }
            />
          ))}
          {!company.projects.length && (
            <tr>
              <td colSpan={3}>Alla projekt har blivit stängda</td>
            </tr>
          )}
        </tbody>
      </Table>
      <StyledModalButton
        className="button primary"
        text="Spara"
        shouldCloseOnEsc={true}
        modalVisible={showSaveModal}
        setModalVisible={setShowSaveModal}
        title="Är du säker på att du vill ändra status på följande projekt?"
        disabled={summary == null || !summary.changedProjects.length}
      >
        {summary != null && (
          <SaveProjectsModal
            projects={summary.changedProjects}
            company={company}
            onSaveProjects={internalOnSaveProjects}
            closeModal={() => setShowSaveModal(false)}
          />
        )}
      </StyledModalButton>
    </Wrapper>
  );
};

interface TableRowProps {
  project: ProjectFormData;
  onStatusChanged: (newStatus: ProjectStatus) => void;
}

export const getProjectStatusName = (status: ProjectStatus): string => {
  switch (status) {
    case ProjectStatus.invoiceComplete:
      return "Slutfakturerad";
    case ProjectStatus.notActive:
      return "Inaktiv";
    case ProjectStatus.ongoing:
      return "Pågående";
    case ProjectStatus.readyForInvoice:
      return "Klar för fakturering";
    default:
      throw new Error(`Unknown status: ${status}`);
  }
};

const TableRowWrapper = styled.tr`
  &:nth-child(odd) {
    background-color: #f9f9f9;
  }
  &:nth-child(even) {
    background-color: #ffffff;
  }

  // Name column
  & td:nth-child(1) {
    // fill remaining width
  }

  // Order date column
  & td:nth-child(2) {
    width: 75px;
  }

  // Current status column
  & td:nth-child(3) {
    width: 200px;
  }

  // New status column
  & td:nth-child(4) {
    width: 200px;
  }

  &.changed {
    background-color: #ffc965;

    &:hover {
      background-color: #ffc250 !important;
    }
  }
`;

const TableRow = ({ project, onStatusChanged }: TableRowProps) => {
  const internalOnStatusChanged = (ev) => {
    const newStatus = parseInt(ev.target.value);

    if (!isNaN(newStatus)) {
      onStatusChanged(newStatus as ProjectStatus);
    }
  };

  const hasNewStatus = () => {
    return project.status !== project.newStatus;
  };

  return (
    <TableRowWrapper
      className={classnames("status", hasNewStatus() && "changed")}
    >
      <td>{project.name}</td>
      <td>{project.orderDate ?? "-"}</td>
      <td>{getProjectStatusName(project.status)}</td>
      <td>
        <select value={project.newStatus} onChange={internalOnStatusChanged}>
          <option value={ProjectStatus.notActive}>
            {getProjectStatusName(ProjectStatus.notActive)}
          </option>
          <option value={ProjectStatus.ongoing}>
            {getProjectStatusName(ProjectStatus.ongoing)}
          </option>
          <option value={ProjectStatus.readyForInvoice}>
            {getProjectStatusName(ProjectStatus.readyForInvoice)}
          </option>
          <option value={ProjectStatus.invoiceComplete}>
            {getProjectStatusName(ProjectStatus.invoiceComplete)}
          </option>
        </select>
      </td>
    </TableRowWrapper>
  );
};

export default Projects;
